import React from 'react';
import { useTestimonials } from '../hooks/useContentful';
import TestimonialCard from './TestimonialCard';

const TestimonialsSection = () => {
  const { testimonials, loading, error } = useTestimonials();
  const staticTestimonials = [
    {
      customerName: "Ana Martínez",
      testimonialText: "Salvaron la vida de mi labrador Max cuando tuvo una torsión gástrica a medianoche. El equipo actuó rápidamente.",
      rating: 5,
      customerImage: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w1NzM4MTl8MHwxfHNlYXJjaHw3fHxwZXJzb24lMjBzbWlsaW5nfGVufDB8fHx8MTcyMTY2OTY5OHww&ixlib=rb-4.0.3&q=80&w=1080",
      delay: "0.6s"
    },
    {
      customerName: "Carlos Rodríguez",
      testimonialText: "Mi gato Simba tuvo un accidente doméstico que requirió cirugía urgente. CEV24 lo atendió con cuidado.",
      rating: 5,
      customerImage: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w1NzM4MTl8MHwxfHNlYXJjaHw3fHxwZXJzb24lMjBzbWlsaW5nfGVufDB8fHx8MTcyMTY2OTY5OHww&ixlib=rb-4.0.3&q=80&w=1080",
      delay: "0.7s"
    },
    {
      customerName: "Familia Pérez",
      testimonialText: "Cuando nuestro bulldog francés tuvo dificultad para respirar, CEV24 nos dio tranquilidad.",
      rating: 4.5,
      customerImage: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w1NzM4MTl8MHwxfHNlYXJjaHw3fHxwZXJzb24lMjBzbWlsaW5nfGVufDB8fHx8MTcyMTY2OTY5OHww&ixlib=rb-4.0.3&q=80&w=1080",
      delay: "0.8s"
    }
  ];

  // Use Contentful data if available, otherwise use static data
  const testimonialsToRender = testimonials.length > 0 ? testimonials : staticTestimonials;

  if (loading) {
    return (
      <section className="py-12 md:py-20 bg-gradient-to-br from-purple-50 to-white relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-300 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="text-center mb-10 md:mb-16">
            <div className="h-8 md:h-10 bg-gradient-to-r from-purple-200 via-purple-300 to-purple-200 rounded-lg w-64 mx-auto mb-4 animate-shimmer-purple"></div>
            <div className="h-5 md:h-6 bg-gradient-to-r from-purple-100 via-purple-200 to-purple-100 rounded-lg w-96 mx-auto animate-shimmer-purple" style={{ animationDelay: '0.3s' }}></div>
          </div>
          {/* Cards Skeleton */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 md:p-8 shadow-md animate-pulse border border-purple-100" style={{ animationDelay: `${index * 0.1}s` }}>
                {/* Stars Skeleton */}
                <div className="flex gap-1 mb-4 h-6">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-5 h-5 bg-gradient-to-r from-purple-200 to-purple-300 rounded animate-pulse" style={{ animationDelay: `${i * 0.1}s` }}></div>
                  ))}
                </div>
                {/* Text Skeleton */}
                <div className="space-y-3 mb-6">
                  <div className="h-4 bg-gradient-to-r from-purple-100 to-purple-200 rounded animate-shimmer-purple"></div>
                  <div className="h-4 bg-gradient-to-r from-purple-100 to-purple-200 rounded animate-shimmer-purple"></div>
                  <div className="h-4 bg-gradient-to-r from-purple-100 to-purple-200 rounded w-3/4 animate-shimmer-purple"></div>
                  <div className="h-4 bg-gradient-to-r from-purple-100 to-purple-200 rounded w-2/3 animate-shimmer-purple"></div>
                </div>
                {/* Avatar & Name Skeleton */}
                <div className="flex items-center pt-6 border-t border-purple-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-300 to-purple-400 rounded-full animate-pulse animate-glow"></div>
                  <div className="ml-4 flex-1 space-y-2">
                    <div className="h-4 bg-gradient-to-r from-purple-200 to-purple-300 rounded w-1/2 animate-shimmer-purple"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    // Fallback to static content
  }

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-purple-50 via-white to-pink-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-purple-200/20 rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-pink-200/15 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }}></div>
      
      {/* Section Separator */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-400 to-pink-400"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12 md:mb-20 animate-fadeInUp">
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="fas fa-heart"/>
            Testimonios Reales
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-800 mb-4 md:mb-6 leading-tight">
            Historias que <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">salvan vidas</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-4xl mx-auto px-4 leading-relaxed">
            Conoce las experiencias de dueños de mascotas cuyas vidas cambiaron gracias a nuestra dedicación, amor y atención veterinaria de excelencia.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 items-start">
          {testimonialsToRender.map((testimonial, index) => (
            <TestimonialCard key={index} testimonial={testimonial} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;